/** The memory graph's thing vocabulary (spec §3.2, §7.1): the avatar each kind is drawn with, the words the globe, the pocket
 * and search use for it, and how a fact reads. */
import type {
  EpistemicStatus,
  FactQualifier,
  MemoryFact,
  QualifierKind,
  ThingKind,
} from "@/api/memory-focus";
import { translate } from "@/i18n";
import { libMemoryMessages } from "@/i18n/messages/lib-memory";
import type en from "@/i18n/messages/lib-memory/en";
import type { MessageArgs } from "@/i18n/types";
export type AvatarKind =
  | "you"
  | "person"
  | "project"
  | "app"
  | "topic"
  | "document";
const AVATAR_KIND: Record<ThingKind, AvatarKind> = {
  user_profile: "you",
  person: "person",
  company: "project",
  project: "project",
  resource: "app",
  account: "app",
  document: "document",
  concept: "topic",
  category: "topic",
};
/** Avatar box in px (spec §7.1, mockup `BASE`); a topic draws an 8px dot inside its box. */
export const AVATAR_SIZE: Record<AvatarKind, number> = {
  you: 28,
  person: 24,
  project: 24,
  app: 22,
  topic: 24,
  document: 24,
};
type Key = keyof typeof en;
const KIND_KEYS: Record<
  ThingKind,
  readonly [Extract<Key, `kind${string}`>, Extract<Key, `word${string}`>]
> = {
  user_profile: ["kindUserProfile", "wordUserProfile"],
  person: ["kindPerson", "wordPerson"],
  company: ["kindCompany", "wordCompany"],
  project: ["kindProject", "wordProject"],
  resource: ["kindResource", "wordResource"],
  account: ["kindAccount", "wordAccount"],
  document: ["kindDocument", "wordDocument"],
  concept: ["kindConcept", "wordConcept"],
  category: ["kindCategory", "wordCategory"],
};
/** Relation types the backend knows: how a fact reads going out, and from its
 *  target coming in (mockup `INVERSE`). Unknown types read as their words. */
const RELATION_KEYS: Readonly<
  Record<
    string,
    readonly [
      Extract<Key, `relation${string}`>,
      Exclude<Extract<Key, `inverse${string}`>, "inverseUnknown">,
    ]
  >
> = {
  works_on: ["relationWorksOn", "inverseWorksOn"],
  works_at: ["relationWorksAt", "inverseWorksAt"],
  belongs_to: ["relationBelongsTo", "inverseBelongsTo"],
  prefers: ["relationPrefers", "inversePrefers"],
  has_goal: ["relationHasGoal", "inverseHasGoal"],
  has_decision: ["relationHasDecision", "inverseHasDecision"],
  has_hypothesis: ["relationHasHypothesis", "inverseHasHypothesis"],
  decided: ["relationDecided", "inverseDecided"],
  built_with: ["relationBuiltWith", "inverseBuiltWith"],
  tracks_work_in: ["relationTracksWorkIn", "inverseTracksWorkIn"],
  bills_with: ["relationBillsWith", "inverseBillsWith"],
  keeps_notes_in: ["relationKeepsNotesIn", "inverseKeepsNotesIn"],
  invested_in: ["relationInvestedIn", "inverseInvestedIn"],
  designs: ["relationDesigns", "inverseDesigns"],
  describes: ["relationDescribes", "inverseDescribes"],
  owns: ["relationOwns", "inverseOwns"],
  depends_on: ["relationDependsOn", "inverseDependsOn"],
  affects: ["relationAffects", "inverseAffects"],
  tracked_in: ["relationTrackedIn", "inverseTrackedIn"],
  uses: ["relationUses", "inverseUses"],
  covers: ["relationCovers", "inverseCovers"],
  documented_in: ["relationDocumentedIn", "inverseDocumentedIn"],
  mentions: ["relationMentions", "inverseMentions"],
  wrote: ["relationWrote", "inverseWrote"],
};
const STATUS_KEY: Record<
  EpistemicStatus,
  Exclude<Extract<Key, `status${string}`>, "statusLine"> | null
> = {
  fact: null,
  goal: "statusGoal",
  decision: "statusDecision",
  hypothesis: "statusHypothesis",
};
const tr = <K extends Key>(key: K, ...args: MessageArgs<(typeof en)[K]>) =>
  translate(libMemoryMessages, key, ...args);
/** A relation going out, lower-case in English ("has goal"). */
const relationWords = (relation: string) => {
  const key = RELATION_KEYS[relation]?.[0];
  return key ? tr(key) : relation.replace(/_/g, " ").trim();
};
export const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
export const avatarKindOf = (kind: ThingKind): AvatarKind =>
  AVATAR_KIND[kind] ?? "topic";
export const kindName = (kind: ThingKind) =>
  tr(KIND_KEYS[kind]?.[0] ?? "kindThing");
/** Node types the user does not read as things Denker learned. */
const NOT_LEARNED_TYPES: ReadonlySet<string> = new Set([
  "user_profile",
  "conversation_summary",
]);
export const isLearnedThingType = (nodeType: string) =>
  !NOT_LEARNED_TYPES.has(nodeType);
/** The kind as a word inside a sentence ("person", "Personen"); never
 *  lower-case a translated `kindName` instead. */
export const kindWord = (kind: ThingKind, count: number): string =>
  tr(KIND_KEYS[kind]?.[1] ?? "wordThing", { count });
/** "38 topics" when no thing of that kind is lit, otherwise "12 more people" (mockup `hiddenGroups`). */
export const bundleLabel = (
  kind: ThingKind,
  count: number,
  shownOfKind: number,
) =>
  tr(shownOfKind > 0 ? "bundleMore" : "bundle", {
    count,
    kind: kindWord(kind, count),
  });
/** You is stored as "profile"; the UI says You (spec §3.2). */
export const thingDisplayName = (thing: { name: string; kind: ThingKind }) =>
  thing.kind === "user_profile" ? tr("you") : thing.name;
/** The line under a thing's name: your own name for You, a person's role, otherwise the kind. */
export function thingKindLine(
  thing: { kind: ThingKind; description: string },
  youName: string,
): string {
  if (thing.kind === "user_profile") return youName;
  const role = thing.description.trim();
  return thing.kind === "person" && role && role.length <= 40
    ? role
    : kindName(thing.kind);
}
/** Initials for people, the first letter for other lettered avatars (mockup `glyphOf`). */
export function thingGlyph(name: string, avatar: AvatarKind): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.charAt(0) ?? "";
  return (
    avatar !== "person" || words.length < 2
      ? first
      : `${first}${words[words.length - 1]?.charAt(0) ?? ""}`
  ).toUpperCase();
}
export const relationGroupKey = (
  fact: Pick<MemoryFact, "source" | "relationship_type">,
  viewpointId: string,
) => `${fact.source.id === viewpointId ? ">" : "<"}${fact.relationship_type}`;
/** A section title for a fact seen from one thing: "Works on" going out, "Worked on by" coming in. */
export function relationGroupLabel(
  fact: Pick<MemoryFact, "source" | "relationship_type">,
  viewpointId: string,
): string {
  const relation = capitalize(relationWords(fact.relationship_type));
  if (fact.source.id === viewpointId) return relation;
  const inverse = RELATION_KEYS[fact.relationship_type]?.[1];
  return inverse ? tr(inverse) : tr("inverseUnknown", { relation });
}
/** "denker-dolcetto → has goal → Homepage H1" (spec §7.2). */
export const factPath = (
  fact: Pick<MemoryFact, "source" | "target" | "relationship_type">,
) =>
  `${thingDisplayName(fact.source)} → ${relationWords(fact.relationship_type)} → ${thingDisplayName(fact.target)}`;
const namesOf = (qualifiers: readonly FactQualifier[], kind: QualifierKind) =>
  qualifiers
    .filter((qualifier) => qualifier.kind === kind)
    .map((qualifier) => qualifier.target.name);
/** "Applies in denker-dolcetto, when writing" or "Applies everywhere" (spec §7.2). */
export function factScopeLine(qualifiers: readonly FactQualifier[]): string {
  const separator = tr("listSeparator");
  const [places, activities, audiences] = [
    namesOf(qualifiers, "applies_to").join(separator),
    namesOf(qualifiers, "activity").join(separator).toLowerCase(),
    namesOf(qualifiers, "audience").join(separator),
  ];
  const phrases = [
    places && tr("scopeIn", { places }),
    activities && tr("scopeWhen", { activities }),
    audiences && tr("scopeFor", { audiences }),
  ].filter(Boolean);
  return phrases.length > 0
    ? tr("appliesTo", { phrases: phrases.join(separator) })
    : tr("appliesEverywhere");
}
/** "‹Status› · Applies in ‹X›", without a status word for a plain fact. */
export function factStatusLine(
  fact: Pick<MemoryFact, "epistemic_status" | "qualifiers">,
): string {
  const statusKey = STATUS_KEY[fact.epistemic_status] ?? null;
  const scope = factScopeLine(fact.qualifiers);
  return statusKey ? tr("statusLine", { status: tr(statusKey), scope }) : scope;
}
/** One qualifier as its own phrase, for the fact editor's remove rows. */
export function qualifierLabel(qualifier: FactQualifier): string {
  const name = qualifier.target.name;
  if (qualifier.kind === "activity")
    return tr("qualifierWhen", { name: name.toLowerCase() });
  return tr(qualifier.kind === "audience" ? "qualifierFor" : "qualifierIn", {
    name,
  });
}
